/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Box, Paper, Group, Image } from '@mantine/core';

// Styles
import classes from './sites.module.css';

// Components
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { Pagination } from '@components/atoms/Pagination';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import {
	IconCirclePlus,
	IconTrash,
} from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Apis
import { useSitesApi } from '@features/resources/sites/api/get-sites';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useProducers } from 'shared/api/get-producers';

// Datatable config
import { tableSchemaBase as columns } from '../../../../../features/resources/sites/sites.columns';

// Utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
	truncateString,
} from '@utils/functions';

// Custom hook
import { useSites } from '@features/resources/sites/hooks/useSites';
import { useRemove } from '@features/resources/sites/api/remove';
import { useRemoveMultiple } from '@features/resources/sites/api/removeMultiple';

import { ConfirmationModal } from '@components/molecules';
import {
	LightButton,
	ResetButton,
	Tooltip,
	Text,
} from '@components/atoms';
import { useMediaQuery } from '@mantine/hooks';
import { DrawerSiteShowMore } from '@features/resources/sites/components/DrawerSiteShowMore';
import { MultipleSelectionSiteModal } from '@features/resources/sites/components/MultipleSelectionSiteModal';
import { TTargetColumn } from '@global-types/index';
import { UpdateSite } from '@features/resources/sites/components/UpdateSite';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Sites = () => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { t } = useTranslation();

	const {
		containerRef,
		sitesPage,
		sitesPageSize,
		handleScroll,
		setResetFilterSites,
		isOverflowFilters,
		showScrollFilters,
		handleapplyprojectfilter,
		ref,
		handlerowviewmore,
		handlerowremoveitem,
		handlechangesortstatus,
		handlePaginationChangeSize,
		handleChangePagination,
		handleapplyproducerfilter,
		openDrawer,
		handledeleteconversionfactor,
		queryParamsProducers,
		resetFilterProjects,
		setResetFilterProjects,
		sitesSortStatus,
		sitesSelectedRow,
		handleCloseDrawerShowMoreSite,
		sitesSortParams,
		sitesQueryParams,
		sitesOpenConfirmationModalDelete,
		handleclosedeleteconfirmationmodal,
		handledeleteconfirmationmodalconfirm,
		setOpenDrawerSiteForm,
		sitesSelectedRecords,
		handleResetSelectedItems,
		handleselectedrecordchange,
		handlerowclicked,
		setSitesSelectedRecords,
		handleConfirmDeleteMultipleItem,
		setOpenSelectedItemsToDeleteModal,
		openSelectedItemsToDeleteModal,
		handleDeleteMultipleItems,
		sitesDatatableOptions,
		setSitesDatatableOptions,
		handleeditsite,
		openEditForm,
		handlecloseeditsite,
		setOpenEditForm,
		setOpenDrawer,
	} = useSites();

	// APIs
	const { data: projects } = useMeasurabilities(
		queryParamsProducers
	);

	const { data: producers } = useProducers();

	const remove = useRemove(sitesPage, sitesPageSize);

	const removeMultiple = useRemoveMultiple(
		sitesPage,
		sitesPageSize
	);

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useSitesApi(
		sitesPage,
		sitesPageSize,
		sitesQueryParams,
		sitesSortParams
	);

	const NBR_SELECTED_ROWS = sitesSelectedRecords.length;

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Drawer Edit site ****************************/}

			<UpdateSite
				opened={openEditForm}
				onClose={handlecloseeditsite}
			/>

			{/**************************** Drawer show more site ****************************/}
			<DrawerSiteShowMore
				opened={openDrawer}
				handledelete={handledeleteconversionfactor}
				handleupdate={() => {
					setOpenEditForm(true);
					setOpenDrawer(false);
				}}
				onClose={handleCloseDrawerShowMoreSite}
				position={'right'}
				data={sitesSelectedRow}
			/>
			{/* Delete Multiple selection modal */}
			<MultipleSelectionSiteModal
				type="delete"
				data={sitesSelectedRecords}
				onConfirm={() =>
					handleConfirmDeleteMultipleItem(removeMultiple)
				}
				onClose={() => {
					setOpenSelectedItemsToDeleteModal(false);
				}}
				onRemove={(item) => {
					const newList = [
						...sitesSelectedRecords,
					].filter((apiToken) => apiToken.id !== item.id);

					setSitesSelectedRecords(newList);
				}}
				opened={openSelectedItemsToDeleteModal}
			/>

			{/**************************** Delete confirmation modal ****************************/}

			<ConfirmationModal
				title={t('sites.modal.delete.title')}
				description={t('sites.modal.delete.description')}
				onConfirm={() =>
					handledeleteconfirmationmodalconfirm(remove)
				}
				onClose={handleclosedeleteconfirmationmodal}
				opened={
					sitesOpenConfirmationModalDelete &&
					sitesOpenConfirmationModalDelete.open
				}
			/>

			{/**************************** Content ****************************/}
			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
											}}
											onApply={handleapplyproducerfilter}
											label={t('global.producer')}
											size="lg"
											options={convertArrayToLabelValue(
												producers
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProducer'
											)}
											placeholder={t(
												'filters.placeholders.selectProducer'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterProjects}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
												setResetFilterSites(_isReset);
											}}
											onApply={handleapplyprojectfilter}
											label={t('global.projects')}
											size="lg"
											options={convertArrayToLabelValue(
												projects
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProject'
											)}
											placeholder={t(
												'filters.placeholders.selectProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.SITES.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									size={isBigScreen ? 'lg' : 'md'}
									onClick={() => {
										setOpenDrawerSiteForm(true);
									}}
								>
									{t('sites.addSite')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				{/* Row actions */}
				{checkAcl(ACLS.SITES.DELETE) && (
					<Box className={classes.containerRow}>
						<Group
							gap={'10px'}
							className={classes.containerRowLeft}
						>
							<Tooltip
								label={NBR_SELECTED_ROWS.toString()}
								isActive={
									NBR_SELECTED_ROWS.toString().length > 4
								}
								position={'left'}
							>
								<Box
									className={
										classes.containerNbrSelection
									}
								>
									<Text
										type="body2"
										color="inputs-texts-icons"
										size="xs"
									>
										{'('}
										{sitesSelectedRecords
											? truncateString(
													NBR_SELECTED_ROWS.toString(),
													4
												)
											: 0}
										{') '}
										{t('global.selected')}
									</Text>
									{/* Reset selection action */}
									<ResetButton
										disabled={
											sitesSelectedRecords &&
											sitesSelectedRecords.length == 0
										}
										variant="transparent"
										size="xs"
										onClick={handleResetSelectedItems}
									/>
								</Box>
							</Tooltip>
							<LightButton
								disabled={true}
								leftSection={<IconTrash size={18} />}
								size={isBigScreen ? 'lg' : 'md'}
								onClick={handleDeleteMultipleItems}
							>
								{t('global.delete')}
							</LightButton>
						</Group>
					</Box>
				)}
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setSitesDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={sitesDatatableOptions}
						data={data}
						onViewMore={handlerowviewmore}
						onRowClick={handlerowclicked}
						onRemoveItem={handlerowremoveitem}
						onEditItem={handleeditsite}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={sitesSelectedRecords}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={sitesSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={sitesPageSize}
					totalitems={totalItems}
					activepage={sitesPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Sites;
